// Bookings (7 October 2026): appointments with a person, classes and events, and call-outs at the client's
// address. `services/bookings`, reached through the gateway at `/v1/bookings/**`. The contract is
// `_docs/modules/bookings-contract.md`.
//
// Hand-typed until the generated client is next fetched from a running stack. The request path is the house
// core, so the envelope, token refresh and ApiError mapping are the same as everywhere.
//
// Owners and admins may do everything. Agents read everything and may create, move, cancel, approve, decline and
// complete bookings, but writes to settings, services, staff, time off and class sessions refuse them (403).
// Creating a service or a person past the plan's limit answers 409 `PLAN_LIMIT_REACHED`.

import { http } from './_core/request.js';
import type { Envelope, Meta } from './_core/envelope.js';
import { isApiError } from './_core/errors.js';

// ── Error codes ────────────────────────────────────────────────────────────

/** 409: somebody else has the time that was asked for. Pick another. */
export const SLOT_TAKEN = 'SLOT_TAKEN';
/** 409: the class session or call-out window has no places left. */
export const FULL = 'FULL';
/** 409: the service is inactive, the time is outside the hours, inside the notice or past the horizon. */
export const NOT_BOOKABLE = 'NOT_BOOKABLE';
/** 409: a client tried to change a booking inside the change cut-off. */
export const TOO_LATE = 'TOO_LATE';
/** 409: the plan allows no more services or people. */
export const PLAN_LIMIT_REACHED = 'PLAN_LIMIT_REACHED';

export const ERROR_CODES = [SLOT_TAKEN, FULL, NOT_BOOKABLE, TOO_LATE, PLAN_LIMIT_REACHED] as const;
export type BookingErrorCode = (typeof ERROR_CODES)[number];

/** The booking error code an error carries, or null when it is not one of ours. */
export function bookingErrorCode(e: unknown): BookingErrorCode | null {
  if (!isApiError(e)) return null;
  return (ERROR_CODES as readonly string[]).includes(e.code) ? (e.code as BookingErrorCode) : null;
}

// ── Shapes ─────────────────────────────────────────────────────────────────

export type ServiceKind = 'appointment' | 'class' | 'callout';
/** `none` (8 October 2026): nobody in particular; booked in the business hours, up to `capacity` at the same time. */
export type StaffMode = 'any' | 'choose' | 'none';
export type LocationKind = 'business' | 'client' | 'online';
export type QuestionType = 'text' | 'number' | 'date' | 'select' | 'yes_no' | 'address';
export type BookingStatus = 'requested' | 'confirmed' | 'declined' | 'cancelled' | 'completed' | 'no_show';
/** Where a booking was made: the console, a flow, a voice call, the inbox, a ticket or the public page. */
export type BookingSource = 'console' | 'flow' | 'voice' | 'inbox' | 'tickets' | 'public' | string;
export type Weekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export const BOOKING_STATUSES: readonly BookingStatus[] = ['requested', 'confirmed', 'declined', 'cancelled', 'completed', 'no_show'];
export const QUESTION_TYPES: readonly QuestionType[] = ['text', 'number', 'date', 'select', 'yes_no', 'address'];

export interface BookingLimits {
  /** People the plan allows; null means no limit. */
  booking_staff: number | null;
  /** Services the plan allows; null means no limit. */
  booking_services: number | null;
}

export interface Settings {
  /** How far apart appointment start times are offered. */
  slot_step_minutes: number;
  /** How soon before a start a booking may still be made. */
  min_notice_minutes: number;
  /** How many days ahead a booking may be made. */
  horizon_days: number;
  /** A client may not change or cancel inside this many hours of the start. */
  change_cutoff_hours: number;
  /** Reminders, in minutes before the start (1 440 is a day). */
  reminder_offsets_minutes: number[];
  /** Whether a service with `requires_approval: null` waits for approval. */
  approval_by_default: boolean;
  confirm_whatsapp: boolean;
  confirm_email: boolean;
  confirm_sms: boolean;
  public_page_enabled: boolean;
  /** The short name in the public page's address. */
  public_slug: string | null;
  business_address: string | null;
  /** Booking numbers start with this, such as `BK`. */
  number_prefix: string;
  /** Read only: the public booking page's address. */
  public_url?: string | null;
  /** Read only: from the plan. */
  limits?: BookingLimits;
  updated_at?: string;
}

export type SettingsUpdate = Omit<Settings, 'public_url' | 'limits' | 'updated_at'>;

export interface CalloutWindow {
  /** `HH:mm` in the organisation's time zone. */
  from: string;
  to: string;
  /** How many call-outs fit in the window. */
  capacity: number;
}

export interface Question {
  /** Stable key the answer is stored under. */
  key: string;
  label: string;
  type: QuestionType;
  required: boolean;
  /** For `select` only. */
  options?: string[];
}

export interface Service {
  id: string;
  name: string;
  description: string | null;
  kind: ServiceKind;
  duration_minutes: number;
  buffer_before_minutes: number;
  buffer_after_minutes: number;
  /** A CSS colour such as `#3b82f6`. */
  colour: string | null;
  /** Shown as written, such as "R350" or "From R200". */
  price_label: string | null;
  /** null follows the settings' `approval_by_default`. */
  requires_approval: boolean | null;
  staff_mode: StaffMode;
  location_kind: LocationKind;
  online_link: string | null;
  /** Places in a class; the default for new sessions. */
  capacity: number | null;
  callout_windows: CalloutWindow[];
  questions: Question[];
  staff_ids: string[];
  sort_order: number;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export type ServiceInput = Omit<Service, 'id' | 'created_at' | 'updated_at' | 'staff_ids'> & { staff_ids?: string[] };

export interface StaffHoursSlot {
  day: Weekday;
  opens: string;
  closes: string;
}

export interface Staff {
  id: string;
  name: string;
  /** The team member this person signs in as, if any. */
  user_id: string | null;
  email: string | null;
  phone: string | null;
  colour: string | null;
  /** null means the business's hours. */
  hours: StaffHoursSlot[] | null;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export type StaffInput = Omit<Staff, 'id' | 'created_at' | 'updated_at'>;

export interface TimeOff {
  id: string;
  staff_id?: string;
  starts_at: string;
  ends_at: string;
  reason: string | null;
}

export interface TimeOffInput {
  starts_at: string;
  ends_at: string;
  reason?: string;
}

export interface Session {
  id: string;
  service_id: string;
  staff_id: string | null;
  starts_at: string;
  ends_at: string;
  capacity: number;
  booked: number;
  places_left: number;
  series_id: string | null;
  cancelled_at: string | null;
}

export interface SessionRepeat {
  /** 1 is Monday, 7 is Sunday. */
  weekdays: number[];
  /** `yyyy-MM-dd`, the last day of the series. */
  until: string;
}

export interface CreateSessionsRequest {
  service_id: string;
  staff_id?: string | null;
  starts_at: string;
  capacity?: number;
  repeat?: SessionRepeat;
}

export interface UpdateSessionRequest {
  starts_at?: string;
  staff_id?: string | null;
  capacity?: number;
}

export interface AvailabilitySlot {
  starts_at: string;
  ends_at: string;
  staff_ids: string[];
}

export interface AvailabilityWindow {
  starts_at: string;
  ends_at: string;
  places_left: number;
}

export interface AvailabilitySession {
  id: string;
  starts_at: string;
  ends_at: string;
  places_left: number;
  staff_id: string | null;
}

export interface Availability {
  kind: ServiceKind;
  slots?: AvailabilitySlot[];
  windows?: AvailabilityWindow[];
  sessions?: AvailabilitySession[];
  timezone: string;
}

export interface AvailabilityQuery {
  service_id: string;
  /** `yyyy-MM-dd` or ISO. */
  from: string;
  to: string;
  staff_id?: string;
}

export interface BookingContactInput {
  name: string;
  phone?: string;
  email?: string;
  contact_id?: string;
  /** The channel's id for the person (a number, an address, a PSID). */
  ref?: string;
}

export interface CreateBookingRequest {
  service_id: string;
  /** The appointment's start; for a call-out, the window's start. */
  starts_at?: string;
  staff_id?: string;
  /** For a class. */
  session_id?: string;
  contact: BookingContactInput;
  address?: string;
  answers?: Record<string, string | number | boolean>;
  notes?: string;
  /** Default `console`. */
  channel?: string;
  conversation_id?: string;
  /** Default true. */
  notify_client?: boolean;
}

export interface BookingContact {
  id: string | null;
  ref: string | null;
  name: string | null;
  phone: string | null;
  email: string | null;
}

export interface Booking {
  id: string;
  /** What the client quotes, such as `BK-1042`. */
  number: string;
  service: { id: string; name: string; kind: ServiceKind };
  staff: { id: string; name: string } | null;
  session_id: string | null;
  starts_at: string;
  ends_at: string;
  status: BookingStatus;
  contact: BookingContact;
  address: string | null;
  answers: Record<string, unknown> | null;
  channel: string | null;
  source: BookingSource | null;
  conversation_id: string | null;
  ticket_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  /** "Thu 9 Oct at 10:00", in the organisation's time zone and language. */
  when_text: string;
}

export interface BookingHistory {
  at: string;
  /** Who did it, in words: a person's name, "Client" or "System". */
  actor: string | null;
  action: string;
  /** What changed, as the service records it: e.g. `{reason}`, `{text}`, `{from, to}` for a move. */
  detail: Record<string, unknown> | string | null;
}

export type BookingDetail = Booking & { history: BookingHistory[] };

export interface BookingListQuery {
  from?: string;
  to?: string;
  status?: BookingStatus | string;
  service_id?: string;
  staff_id?: string;
  q?: string;
  contact_ref?: string;
  cursor?: string;
  limit?: number;
}

export interface BookingListMeta extends Meta {
  next_cursor?: string | null;
}

export interface BookingList {
  data: Booking[];
  meta?: BookingListMeta;
}

export interface MoveBookingRequest {
  starts_at: string;
  staff_id?: string;
  session_id?: string;
  notify_client?: boolean;
}

export interface CancelBookingRequest {
  reason?: string;
  notify_client?: boolean;
}

// ── Settings ──────────────────────────────────────────────────────────────

const enc = encodeURIComponent;

export function getSettings(): Promise<Envelope<Settings>> {
  return http.get<Envelope<Settings>>('/v1/bookings/settings');
}

/** Owners and admins. */
export function saveSettings(req: SettingsUpdate): Promise<Envelope<Settings>> {
  return http.put<Envelope<Settings>>('/v1/bookings/settings', { body: req });
}

// ── Services ──────────────────────────────────────────────────────────────

export function listServices(opts?: { include_inactive?: boolean }): Promise<Envelope<Service[]>> {
  return http.get<Envelope<Service[]>>('/v1/bookings/services', {
    query: opts?.include_inactive ? { include_inactive: true } : undefined
  });
}

/** Owners and admins. 409 `PLAN_LIMIT_REACHED` past the plan's `booking_services`. */
export function createService(req: Partial<ServiceInput> & { name: string; kind: ServiceKind }): Promise<Envelope<Service>> {
  return http.post<Envelope<Service>>('/v1/bookings/services', { body: req });
}

export function updateService(id: string, req: Partial<ServiceInput>): Promise<Envelope<Service>> {
  return http.patch<Envelope<Service>>(`/v1/bookings/services/${enc(id)}`, { body: req });
}

/** Soft: the service stops being offered, its bookings stay. */
export function deleteService(id: string): Promise<void> {
  return http.delete<void>(`/v1/bookings/services/${enc(id)}`);
}

/** Who does this service: replaces the list. */
export function setServiceStaff(id: string, staffIds: string[]): Promise<Envelope<Service>> {
  return http.put<Envelope<Service>>(`/v1/bookings/services/${enc(id)}/staff`, { body: { staff_ids: staffIds } });
}

// ── Staff ─────────────────────────────────────────────────────────────────

export function listStaff(): Promise<Envelope<Staff[]>> {
  return http.get<Envelope<Staff[]>>('/v1/bookings/staff');
}

/** Owners and admins. 409 `PLAN_LIMIT_REACHED` past the plan's `booking_staff`. */
export function createStaff(req: Partial<StaffInput> & { name: string }): Promise<Envelope<Staff>> {
  return http.post<Envelope<Staff>>('/v1/bookings/staff', { body: req });
}

export function updateStaff(id: string, req: Partial<StaffInput>): Promise<Envelope<Staff>> {
  return http.patch<Envelope<Staff>>(`/v1/bookings/staff/${enc(id)}`, { body: req });
}

export function deleteStaff(id: string): Promise<void> {
  return http.delete<void>(`/v1/bookings/staff/${enc(id)}`);
}

export function listTimeOff(staffId: string, opts?: { from?: string; to?: string }): Promise<Envelope<TimeOff[]>> {
  return http.get<Envelope<TimeOff[]>>(`/v1/bookings/staff/${enc(staffId)}/time-off`, {
    query: { from: opts?.from, to: opts?.to }
  });
}

export function addTimeOff(staffId: string, req: TimeOffInput): Promise<Envelope<TimeOff>> {
  return http.post<Envelope<TimeOff>>(`/v1/bookings/staff/${enc(staffId)}/time-off`, { body: req });
}

export function removeTimeOff(staffId: string, timeOffId: string): Promise<void> {
  return http.delete<void>(`/v1/bookings/staff/${enc(staffId)}/time-off/${enc(timeOffId)}`);
}

// ── Class sessions ────────────────────────────────────────────────────────

export function listSessions(opts?: { from?: string; to?: string; service_id?: string }): Promise<Envelope<Session[]>> {
  return http.get<Envelope<Session[]>>('/v1/bookings/sessions', {
    query: { from: opts?.from, to: opts?.to, service_id: opts?.service_id }
  });
}

/** One session, or a weekly series with `repeat`. Answers every session made. */
export function createSessions(req: CreateSessionsRequest): Promise<Envelope<Session[]>> {
  return http.post<Envelope<Session[]>>('/v1/bookings/sessions', { body: req });
}

export function updateSession(id: string, req: UpdateSessionRequest): Promise<Envelope<Session>> {
  return http.patch<Envelope<Session>>(`/v1/bookings/sessions/${enc(id)}`, { body: req });
}

/** Its bookings are cancelled, and the clients told when `notify_clients` is true. */
export function cancelSession(id: string, req?: { reason?: string; notify_clients?: boolean }): Promise<Envelope<Session>> {
  return http.post<Envelope<Session>>(`/v1/bookings/sessions/${enc(id)}/cancel`, {
    body: { notify_clients: true, ...req }
  });
}

// ── Availability ──────────────────────────────────────────────────────────

export function getAvailability(q: AvailabilityQuery): Promise<Envelope<Availability>> {
  return http.get<Envelope<Availability>>('/v1/bookings/availability', {
    query: { service_id: q.service_id, from: q.from, to: q.to, staff_id: q.staff_id }
  });
}

// ── Bookings ──────────────────────────────────────────────────────────────

export function listBookings(q?: BookingListQuery): Promise<BookingList> {
  return http.get<BookingList>('/v1/bookings', {
    query: q ? {
      from: q.from, to: q.to, status: q.status, service_id: q.service_id, staff_id: q.staff_id,
      q: q.q, contact_ref: q.contact_ref, cursor: q.cursor, limit: q.limit
    } : undefined
  });
}

export function getBooking(id: string): Promise<Envelope<BookingDetail>> {
  return http.get<Envelope<BookingDetail>>(`/v1/bookings/${enc(id)}`);
}

/** 409 `SLOT_TAKEN`, `FULL` or `NOT_BOOKABLE`. A service needing approval makes a `requested` booking. */
export function createBooking(req: CreateBookingRequest): Promise<Envelope<Booking>> {
  return http.post<Envelope<Booking>>('/v1/bookings', { body: req });
}

export function approveBooking(id: string): Promise<Envelope<Booking>> {
  return http.post<Envelope<Booking>>(`/v1/bookings/${enc(id)}/approve`);
}

export function declineBooking(id: string, reason?: string): Promise<Envelope<Booking>> {
  return http.post<Envelope<Booking>>(`/v1/bookings/${enc(id)}/decline`, { body: reason ? { reason } : {} });
}

/** 409 `SLOT_TAKEN`. */
export function moveBooking(id: string, req: MoveBookingRequest): Promise<Envelope<Booking>> {
  return http.post<Envelope<Booking>>(`/v1/bookings/${enc(id)}/move`, { body: { notify_client: true, ...req } });
}

export function cancelBooking(id: string, req?: CancelBookingRequest): Promise<Envelope<Booking>> {
  return http.post<Envelope<Booking>>(`/v1/bookings/${enc(id)}/cancel`, { body: { notify_client: true, ...req } });
}

export function completeBooking(id: string): Promise<Envelope<Booking>> {
  return http.post<Envelope<Booking>>(`/v1/bookings/${enc(id)}/complete`);
}

export function markNoShow(id: string): Promise<Envelope<Booking>> {
  return http.post<Envelope<Booking>>(`/v1/bookings/${enc(id)}/no-show`);
}

export function addBookingNote(id: string, text: string): Promise<Envelope<Booking>> {
  return http.post<Envelope<Booking>>(`/v1/bookings/${enc(id)}/notes`, { body: { text } });
}

// ── Calendar sync (8 October 2026) ─────────────────────────────────────────
// A bookable person, or the business (no staff_id), connects a Google or Microsoft calendar. Bookings are written to
// it and its busy times stop those times being offered. Owners and admins only.

export const CALENDAR_SYNC_UNAVAILABLE = 'CALENDAR_SYNC_UNAVAILABLE';
export const CALENDAR_PROVIDER_NOT_CONFIGURED = 'CALENDAR_PROVIDER_NOT_CONFIGURED';
export const CALENDAR_LINK_EXPIRED = 'CALENDAR_LINK_EXPIRED';
export const CALENDAR_NOT_YOURS = 'CALENDAR_NOT_YOURS';
export const CALENDAR_REFUSED = 'CALENDAR_REFUSED';

export type CalendarProvider = 'google' | 'microsoft';
export type CalendarConnectionStatus = 'connected' | 'error';

export interface CalendarConnection {
  id: string;
  /** null: the business's own calendar. */
  staff_id: string | null;
  staff_name: string | null;
  provider: CalendarProvider;
  account_email: string | null;
  status: CalendarConnectionStatus;
  last_error: string | null;
  last_synced_at: string | null;
  created_at: string;
}

export interface CalendarOverview {
  /** The plan includes calendar sync (`calendar_sync`). */
  allowed: boolean;
  plan_name: string | null;
  /** Which providers the platform has set up. */
  available: Record<CalendarProvider, boolean>;
  connections: CalendarConnection[];
}

export function getCalendars(): Promise<Envelope<CalendarOverview>> {
  return http.get<Envelope<CalendarOverview>>('/v1/bookings/calendar');
}

/** Answers the provider's sign-in address to open. 403 `PLAN_LIMIT_REACHED`, 503 `CALENDAR_SYNC_UNAVAILABLE` or `CALENDAR_PROVIDER_NOT_CONFIGURED`. */
export function connectCalendar(provider: CalendarProvider, staffId?: string | null): Promise<Envelope<{ url: string }>> {
  return http.post<Envelope<{ url: string }>>('/v1/bookings/calendar/connect', { body: { provider, staff_id: staffId ?? null } });
}

/** Finishes a connection with what the provider sent back to the console (`?provider&code&state`). */
export function completeCalendar(provider: CalendarProvider, code: string, state: string): Promise<Envelope<CalendarConnection>> {
  return http.post<Envelope<CalendarConnection>>('/v1/bookings/calendar/complete', { body: { provider, code, state } });
}

/** Removes the platform's upcoming events from that calendar, then forgets it. */
export function disconnectCalendar(id: string): Promise<void> {
  return http.delete<void>(`/v1/bookings/calendar/${enc(id)}`);
}
